// Shop-front art for the places the owner's tour found bare: Hachi's gun stall, the Ghost Lot garage gate and lift,
// Okubo Yard Spray's booth, and a tall lit terminal plus ground ring for every fixer
// kiosk. Upright pieces are props (y-sorted, no collision, feet just north of the counter so the player stands in
// front); ground marks are painted after the baked lights by `placeGround`. Presentation only. Called from hd-scene.ts.
import { WORLD, type Rect } from '../../common/src/data';
import { TILE, inTileRect, boothFootprint, boothFeet } from '../../common/src/map';
import { canvas } from './canvas';
import { poiColor } from './city-art';
import { type G, K, px, frame, disc, dk, lt, alpha, text, textW } from './hd-kit';
import type { HdLight, HdProp } from './hd-scene';
import { hachiStudyStall } from './hd-hachi-study';

type Prop = (cv: HTMLCanvasElement, wx: number, wy: number, ax: number, ay: number) => void;
interface Sprite { cv: HTMLCanvasElement; ax: number; ay: number }

/** a thick straight line of 1-px steps */
function line(g: G, x0: number, y0: number, x1: number, y1: number, t: number, c: (i: number) => string) {
  const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0));
  for (let i = 0; i <= n; i++) px(g, Math.round(x0 + (x1 - x0) * i / n), Math.round(y0 + (y1 - y0) * i / n), t, t, c(i));
}
const neonFrame = (g: G, x: number, y: number, w: number, h: number, c: string) => {
  px(g, x, y, w, h, K.ink); px(g, x + 1, y + 1, w - 2, h - 2, dk(c, 0.8)); frame(g, x + 2, y + 2, w - 4, h - 4, c); frame(g, x + 3, y + 3, w - 6, h - 6, dk(c, 0.45));
};
function glow(g: G, x: number, y: number, rx: number, ry: number, col: string, a: number) {
  g.save(); g.globalCompositeOperation = 'lighter'; g.translate(x, y); g.scale(1, ry / rx);
  const grd = g.createRadialGradient(0, 0, 0, 0, 0, rx); grd.addColorStop(0, alpha(col, a)); grd.addColorStop(1, alpha(col, 0));
  g.fillStyle = grd; g.fillRect(-rx, -rx, rx * 2, rx * 2); g.restore();
}

// ---------------------------------------------------------------- Hachi's Hardware & Hot Lead: a gun stall
// ---------------------------------------------------------------- Ghost Lot Garage: a gate arch, barrier and a car lift
function ghostGate(): Sprite {
  const W = 210, H = 134, [cv, g] = canvas(W, H);
  for (const x of [8, 186]) {
    px(g, x, 34, 16, H - 36, K.ink); px(g, x + 1, 35, 14, H - 38, K.concrete); px(g, x + 1, 35, 3, H - 38, K.concreteHi);
    for (let k = 0; k < 4; k++) px(g, x + 1, H - 36 + k * 8, 14, 4, K.amber);
  }
  px(g, 4, 20, 202, 18, K.ink); px(g, 5, 21, 200, 16, K.metalLo); px(g, 5, 21, 200, 2, K.metalHi);
  neonFrame(g, 24, 2, 162, 32, K.amber);
  px(g, 34, 10, 16, 14, K.white); px(g, 36, 6, 12, 6, K.white); // the ghost: round head, sheet body, wavy hem
  for (const x of [34, 40, 46]) px(g, x, 24, 4, 3, K.white);
  px(g, 38, 12, 2, 3, K.ink); px(g, 44, 12, 2, 3, K.ink); px(g, 40, 18, 4, 2, K.ink);
  text(g, 'GHOST', 62, 11, lt(K.amber, 0.55), 2); text(g, 'LOT', 128, 11, lt(K.amber, 0.55), 2);
  for (const x of [60, 148]) { px(g, x, 38, 2, 4, K.metalLo); px(g, x - 3, 42, 8, 4, K.amber); }
  // raised barrier arm on the right post
  line(g, 192, H - 52, 168, H - 98, 4, i => (i >> 3) & 1 ? K.red : K.white);
  px(g, 190, H - 56, 10, 8, K.ink);
  return { cv, ax: W / 2, ay: H - 3 };
}
/** a car on a two-post lift beside a tool chest (garage and chop yards) */
function lift(body: string): Sprite {
  const W = 120, H = 100, [cv, g] = canvas(W, H);
  px(g, 4, H - 6, W - 8, 4, alpha(K.night, 0.5));
  for (const x of [8, 94]) { px(g, x, 8, 14, H - 12, K.ink); px(g, x + 1, 9, 12, H - 14, K.metal); px(g, x + 1, 9, 3, H - 14, K.metalHi); for (let k = 0; k < 3; k++) px(g, x + 1, H - 18 + k * 5, 12, 2, (k & 1) ? K.ink : K.amber); }
  px(g, 22, 52, 72, 5, K.ink); px(g, 22, 53, 72, 2, K.amber); // lift arm
  px(g, 26, 36, 64, 16, dk(body, 0.2)); px(g, 26, 36, 64, 2, lt(body, 0.3)); px(g, 40, 26, 36, 12, K.ink); px(g, 42, 28, 32, 8, K.glassHi); // raised car, side on
  px(g, 24, 44, 5, 5, K.amber); px(g, 87, 44, 4, 5, K.pink);
  disc(g, 36, 54, 8, 8, K.ink); disc(g, 36, 54, 4, 4, K.metalHi); disc(g, 80, 54, 8, 8, K.ink); disc(g, 80, 54, 4, 4, K.metalHi);
  px(g, 100, H - 30, 18, 26, K.ink); px(g, 101, H - 29, 16, 24, K.red); for (let k = 0; k < 3; k++) { px(g, 102, H - 27 + k * 7, 14, 5, K.redLo); px(g, 107, H - 25 + k * 7, 4, 1, K.metalHi); } // tool chest
  return { cv, ax: W / 2, ay: H - 3 };
}

// ---------------------------------------------------------------- Okubo Yard Spray: a spray booth and paint drums
function sprayBooth(): Sprite {
  const W = 170, H = 140, [cv, g] = canvas(W, H);
  px(g, 4, H - 7, W - 8, 5, alpha(K.night, 0.5));
  px(g, 10, 40, 150, 94, K.ink); px(g, 13, 43, 144, 82, dk(K.pink, 0.85)); // open front onto a pink-lit interior
  for (let j = 0; j < 4; j++) px(g, 13, 43 + j * 20, 144, 20, alpha(K.pink, 0.08 + j * 0.05));
  for (let i = 14; i < 156; i += 6) px(g, i, 46, 2, 18, alpha(K.ink, 0.5)); // filter wall
  px(g, 42, 62, 86, 26, K.metalLo); px(g, 48, 56, 74, 10, K.ink); px(g, 52, 58, 66, 6, K.glassHi); // a car half way through its respray
  px(g, 42, 76, 43, 26, K.pink); px(g, 85, 76, 43, 26, K.metal); px(g, 42, 76, 43, 2, lt(K.pink, 0.45));
  px(g, 46, 82, 9, 6, K.amber); px(g, 116, 82, 9, 6, K.amber); px(g, 40, 100, 90, 4, K.ink);
  disc(g, 56, 106, 8, 5, K.ink); disc(g, 114, 106, 8, 5, K.ink);
  for (const x of [28, 60, 100, 132]) px(g, x, 43, 8, 70, alpha(K.white, 0.14)); // hanging plastic curtain
  px(g, 10, 124, 150, 10, K.concreteLo); px(g, 10, 124, 150, 1, K.concreteHi);
  for (const x of [4, 158]) { px(g, x, 40, 8, 94, K.ink); for (let k = 0; k < 9; k++) px(g, x + 1, 42 + k * 10, 6, 5, K.amber); } // hazard posts
  px(g, 4, 22, 162, 20, K.ink); px(g, 6, 24, 158, 16, K.metal); px(g, 6, 24, 158, 2, K.metalHi); // roof with extraction duct
  px(g, 104, 8, 26, 18, K.ink); px(g, 106, 10, 22, 14, K.metalLo); disc(g, 117, 17, 6, 6, K.ink); disc(g, 117, 17, 3, 3, K.metalHi);
  neonFrame(g, 30, 0, 100, 24, K.pink);
  px(g, 38, 6, 6, 13, K.white); px(g, 37, 4, 8, 3, K.cyan); px(g, 33, 8, 3, 1, K.pink); px(g, 31, 11, 4, 1, K.pink); px(g, 33, 14, 3, 1, K.pink); // spray can and mist
  text(g, 'SPRAY', 52, 6, lt(K.pink, 0.65), 2);
  px(g, 122, 8, 4, 4, K.cyan);
  return { cv, ax: W / 2, ay: H - 3 };
}
function drums(): Sprite {
  const W = 58, H = 54, [cv, g] = canvas(W, H);
  px(g, 2, H - 6, W - 4, 4, alpha(K.night, 0.5));
  [[2, 14, K.pink], [20, 20, K.cyan], [38, 12, K.amber]].forEach(([x, y, c], i) => {
    const dx = x as number, dy = y as number, col = c as string;
    px(g, dx, dy, 17, 30, K.ink); px(g, dx + 1, dy + 2, 15, 27, dk(col, 0.35)); px(g, dx + 1, dy + 2, 4, 27, col);
    px(g, dx + 1, dy + 9, 15, 2, K.ink); px(g, dx + 1, dy + 20, 15, 2, K.ink);
    disc(g, dx + 8, dy + 2, 8, 3, K.ink); disc(g, dx + 8, dy + 2, 7, 2, K.metalHi);
    px(g, dx + 6, dy + 12, 8, 6, K.white); px(g, dx + 7, dy + 14, 6, 1, i === 1 ? K.cyan : K.red);
  });
  return { cv, ax: W / 2, ay: H - 3 };
}

// ---------------------------------------------------------------- Fixer kiosk: a tall lit terminal
function kioskTerminal(c: string): Sprite {
  const W = 36, H = 76, [cv, g] = canvas(W, H);
  px(g, 3, H - 6, W - 6, 4, alpha(K.night, 0.5));
  px(g, 6, H - 12, 24, 8, K.ink); px(g, 7, H - 11, 22, 6, K.metalLo); // plinth
  px(g, 8, 22, 20, H - 32, K.ink); px(g, 9, 23, 18, H - 34, K.metal); px(g, 9, 23, 3, H - 34, K.metalHi); // cabinet
  px(g, 11, 26, 14, 16, K.ink); px(g, 12, 27, 12, 14, dk(c, 0.55)); px(g, 12, 27, 12, 2, lt(c, 0.5)); // lit screen with a briefcase
  px(g, 14, 33, 8, 6, c); px(g, 16, 31, 4, 2, c); px(g, 14, 35, 8, 1, K.ink);
  px(g, 11, 46, 14, 3, K.ink); for (let i = 0; i < 3; i++) px(g, 12 + i * 5, 47, 3, 1, c); // keypad strip
  px(g, 11, 54, 14, 3, K.ink);
  px(g, 17, 8, 2, 14, K.metalHi); // mast, holo diamond above it
  px(g, 17, 0, 2, 2, c); px(g, 15, 2, 6, 2, c); px(g, 13, 4, 10, 2, lt(c, 0.5)); px(g, 15, 6, 6, 2, c); px(g, 17, 8, 2, 2, c);
  return { cv, ax: W / 2, ay: H - 5 };
}

/** Place the upright pieces and lights for the places inside scene rect R. */
export function placeArt(R: Rect, prop: Prop, lights: HdLight[]) {
  const at = (id: string) => { const p = WORLD.pois.find(q => q.id === id); return p && inTileRect(Math.floor(p.x), Math.floor(p.y), R) ? { x: p.x * TILE, y: p.y * TILE } : null; };
  const put = (s: Sprite, x: number, y: number) => prop(s.cv, x, y, s.ax, s.ay);
  const lamp = (x: number, y: number, w: number, h: number, c: string, a: number, pool = false) => lights.push({ x, y, w, h, color: c, alpha: a, ...(pool ? { pool: true, refl: true } : {}) });
  let p = at('weapons');
  if (p) { const f = boothFeet(boothFootprint(WORLD.pois.find(q => q.id === 'weapons')!)!); put(hachiStudyStall(), f.x, f.y); /* on its solid footprint */ lamp(p.x - 36, p.y - 86, 72, 14, K.pink, 0.8); lamp(p.x - 40, p.y - 40, 80, 20, K.amber, 0.5, true); }
  p = at('garage');
  if (p) {
    // the gate's top clears the building north of the lot (lot y18-27, counter y23); the lift backs onto the west wall (x100)
    put(ghostGate(), p.x, p.y - 10); put(lift(K.cyan), p.x - 50, p.y + 44);
    lamp(p.x - 50, p.y - 66, 100, 16, K.amber, 0.75); lamp(p.x - 40, p.y - 44, 80, 20, K.amber, 0.4, true);
  }
  p = at('chop2');
  if (p) {
    put(sprayBooth(), p.x, p.y - 34); put(lift(K.pink), p.x - 76, p.y + 6); put(drums(), p.x + 62, p.y - 6);
    lamp(p.x - 40, p.y - 94, 80, 16, K.pink, 0.8); lamp(p.x - 44, p.y - 56, 88, 30, K.pink, 0.45, true);
  }
  const kc = poiColor('kiosk');
  for (const k of WORLD.pois) if (k.kind === 'kiosk' && inTileRect(Math.floor(k.x), Math.floor(k.y), R)) {
    const x = k.x * TILE, y = k.y * TILE + 5;
    put(kioskTerminal(kc), x, y);
    lamp(x - 8, y - 34, 16, 12, kc, 0.8); lamp(x - 24, y - 6, 48, 18, kc, 0.55, true);
  }
}

/** Ground marks (hd px, painted after the baked lights): the bay and stencils of each place, and a fixer-coloured ring under every kiosk. */
export function placeGround(g: G, R: Rect) {
  const at = (id: string) => { const p = WORLD.pois.find(q => q.id === id); return p && inTileRect(Math.floor(p.x), Math.floor(p.y), R) ? { x: (p.x * TILE - R.x * TILE) * 2, y: (p.y * TILE - R.y * TILE) * 2 } : null; };
  const corners = (x0: number, y0: number, x1: number, y1: number, c: string, n = 16) => {
    for (const [x, y, sx, sy] of [[x0, y0, 1, 1], [x1, y0, -1, 1], [x0, y1, 1, -1], [x1, y1, -1, -1]]) {
      px(g, Math.min(x, x + sx * n), y - (sy < 0 ? 2 : 0), n, 2, c); px(g, x - (sx < 0 ? 2 : 0), Math.min(y, y + sy * n), 2, n, c);
    }
  };
  const chevrons = (cx: number, cy: number, n: number, c: string) => { for (let i = 0; i < n; i++) { const y = cy - i * 14; line(g, cx - 12, y + 7, cx, y, 3, () => c); line(g, cx + 12, y + 7, cx, y, 3, () => c); } };
  const stencil = (t: string, cx: number, cy: number, s: number, c: string) => text(g, t, Math.round(cx - textW(t, s) / 2), cy, c, s);
  let p = at('garage');
  if (p) {
    corners(p.x - 70, p.y - 56, p.x + 70, p.y + 84, alpha(K.amber, 0.7), 18);
    px(g, p.x - 70, p.y + 84, 140, 3, alpha(K.amber, 0.35)); for (const x of [-46, 0, 46]) px(g, p.x + x - 10, p.y + 70, 20, 4, alpha(K.white, 0.4)); // wheel stops
    chevrons(p.x, p.y - 44, 2, alpha(K.amber, 0.6)); stencil('P', p.x, p.y + 38, 4, alpha(K.amber, 0.5));
  }
  p = at('chop2');
  if (p) {
    for (const [dx, dy, rx, ry, c] of [[-26, -60, 62, 22, K.pink], [78, -34, 40, 16, K.cyan], [-84, -18, 34, 14, K.amber], [10, 44, 50, 14, K.pink]] as [number, number, number, number, string][]) {
      g.save(); g.translate(p.x + dx, p.y + dy); g.scale(1, ry / rx); g.fillStyle = alpha(c, 0.16); g.beginPath(); g.arc(0, 0, rx, 0, Math.PI * 2); g.fill();
      g.fillStyle = alpha(c, 0.12); g.beginPath(); g.arc(rx * 0.2, 0, rx * 0.6, 0, Math.PI * 2); g.fill(); g.restore();
    }
    for (const dx of [-14, 14]) for (let y = -52; y < 68; y += 6) px(g, p.x + dx, p.y + y, 2, 4, alpha(K.ink, 0.35)); // tyre tracks into the booth
    stencil('SPRAY', p.x, p.y + 50, 3, alpha(K.pink, 0.55));
    corners(p.x - 66, p.y - 52, p.x + 66, p.y + 76, alpha(K.pink, 0.55), 14);
  }
  const kc = poiColor('kiosk');
  for (const k of WORLD.pois) if (k.kind === 'kiosk' && inTileRect(Math.floor(k.x), Math.floor(k.y), R)) {
    const x = (k.x * TILE - R.x * TILE) * 2, y = (k.y * TILE - R.y * TILE) * 2 + 4;
    g.save(); g.translate(x, y); g.scale(1, 0.62);
    g.fillStyle = alpha(kc, 0.16); g.beginPath(); g.arc(0, 0, 25, 0, Math.PI * 2); g.fill();
    g.lineWidth = 3; g.strokeStyle = alpha(kc, 0.85); g.beginPath(); g.arc(0, 0, 25, 0, Math.PI * 2); g.stroke();
    g.lineWidth = 1; g.strokeStyle = alpha(kc, 0.45); g.beginPath(); g.arc(0, 0, 19, 0, Math.PI * 2); g.stroke(); g.restore();
    for (const sx of [-1, 1]) px(g, x + sx * 28 - 2, y - 2, 4, 4, kc);
  }
}
